/**
 * Authentication Methods API Service
 * Provides methods for managing Azure AD authentication method configurations.
 *
 * Supports all 7 new authentication methods:
 * - SMS
 * - Email OTP
 * - FIDO2 Security Keys
 * - Voice Call
 * - Software OATH Tokens
 * - Hardware OATH Tokens
 * - X.509 Certificate
 *
 * Uses Bearer token authentication via MSAL.
 */

import { ApiService } from './api';
import type {
  AuthenticationMethodConfigRequest,
  AuthenticationMethodConfigResponse,
} from '../types/authenticationMethods';

/**
 * Authentication Methods API Client
 */
class AuthenticationMethodsApiServiceClass {
  /**
   * Get base URL for authentication method endpoints.
   */
  private getBaseUrl(tenantId: string): string {
    return `/api/tenants/${tenantId}/security/policies/authentication-methods`;
  }

  /**
   * Get authentication method configuration for a tenant.
   *
   * @param tenantId - Tenant ID
   * @param methodKey - Authentication method key (e.g., 'sms', 'email', 'fido2')
   * @returns Authentication method configuration
   */
  async getMethodConfiguration(
    tenantId: string,
    methodKey: string
  ): Promise<AuthenticationMethodConfigResponse> {
    return ApiService.request<AuthenticationMethodConfigResponse>(
      `${this.getBaseUrl(tenantId)}/${methodKey}`,
      { method: 'GET' }
    );
  }

  /**
   * Get all authentication method configurations in a single request (batch).
   * This avoids eventual consistency issues from parallel requests to Graph API.
   *
   * @param tenantId - Tenant ID
   * @returns Array of all authentication method configurations
   */
  async getAllMethodConfigurations(
    tenantId: string
  ): Promise<AuthenticationMethodConfigResponse[]> {
    return ApiService.request<AuthenticationMethodConfigResponse[]>(
      `${this.getBaseUrl(tenantId)}`,
      { method: 'GET' }
    );
  }

  /**
   * Update authentication method configuration for a tenant.
   *
   * @param tenantId - Tenant ID
   * @param methodKey - Authentication method key (e.g., 'sms', 'email', 'fido2')
   * @param config - Configuration update request
   * @returns Updated authentication method configuration
   */
  async updateMethodConfiguration(
    tenantId: string,
    methodKey: string,
    config: AuthenticationMethodConfigRequest
  ): Promise<AuthenticationMethodConfigResponse> {
    return ApiService.request<AuthenticationMethodConfigResponse>(
      `${this.getBaseUrl(tenantId)}/${methodKey}`,
      {
        method: 'PUT',
        body: JSON.stringify(config),
      }
    );
  }
}

export const AuthenticationMethodsApiService = new AuthenticationMethodsApiServiceClass();
